import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  Description as LicenseIcon,
  Security as SecurityIcon,
  Computer as ComputerIcon,
  Apps as AppsIcon,
  Assignment as AssignmentIcon,
  Business as ClientsIcon,
  Psychology as AiSystemsIcon,
} from "@mui/icons-material";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import api from "../utils/api";
import DashboardInfoTile from "../components/DashboardInfoTile";
import {
  aiSystemsTileStats,
  clientTileStats,
  formatDashboardCurrency,
} from "../utils/dashboardSummary";
import { formatUkDate, formatUkDateTime } from "../utils/dateLocale";
import { DASHBOARD_TILE_STYLES } from "../constants/nikaoBrand";

const TILE_ICON_PROPS = { sx: { fontSize: 26, flexShrink: 0 } };

const ChartLegend = ({ items, total }) => (
  <Box display="flex" flexWrap="wrap" justifyContent="center" gap={1} mt={0.5}>
    {items.map((item) => {
      const isZero = !item.value;
      const pct =
        total > 0 && item.value ? Math.round((item.value / total) * 100) : 0;
      return (
        <Box
          key={item.name}
          display="flex"
          alignItems="center"
          gap={0.75}
          sx={{ opacity: isZero ? 0.45 : 1 }}
        >
          <Box
            width={8}
            height={8}
            borderRadius="50%"
            flexShrink={0}
            sx={{ backgroundColor: item.color }}
          />
          <Typography
            variant="caption"
            color="text.primary"
            sx={{ lineHeight: 1.3 }}
          >
            {item.name}
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
              ml={0.5}
            >
              {item.value}
              {!isZero && total > 0 ? ` · ${pct}%` : ""}
            </Typography>
          </Typography>
        </Box>
      );
    })}
  </Box>
);

const LicenseDonutChart = ({ data, legendItems, emptyMessage }) => {
  const chartData = data.filter((item) => item.value > 0);
  const total = (legendItems || data).reduce(
    (sum, item) => sum + (Number(item.value) || 0),
    0,
  );

  if (chartData.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
        minHeight={0}
      >
        <Typography color="text.secondary">
          {emptyMessage || "No data available"}
        </Typography>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" height="100%" minHeight={0}>
      <Box position="relative" flex={1} minHeight={0}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius="42%"
              outerRadius="68%"
              paddingAngle={chartData.length > 1 ? 2 : 0}
              dataKey="value"
              stroke="none"
              isAnimationActive={false}
            >
              {chartData.map((entry) => (
                <Cell key={entry.name} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value, name) => {
                const pct = total > 0 ? Math.round((value / total) * 100) : 0;
                return [`${value} licenses (${pct}%)`, name];
              }}
            />
          </PieChart>
        </ResponsiveContainer>
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          pointerEvents="none"
        >
          <Typography variant="h6" fontWeight={700} lineHeight={1.1}>
            {total}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            licenses
          </Typography>
        </Box>
      </Box>
      <ChartLegend items={legendItems || data} total={total} />
    </Box>
  );
};

const RefreshCaption = ({ value }) => {
  if (!value) return null;
  const label = `Last refresh: ${formatUkDateTime(value)}`;
  return (
    <Typography
      component="div"
      fontSize="0.68rem"
      color="text.secondary"
      title={label}
      display="block"
      width="100%"
      maxWidth="100%"
      overflow="hidden"
      textOverflow="ellipsis"
      whiteSpace="nowrap"
      lineHeight={1.3}
    >
      {label}
    </Typography>
  );
};

const AttestationStat = ({ label, value, valueColor }) => (
  <Box flex="1 1 140px" minWidth={120}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="subtitle1" fontWeight={600} color={valueColor}>
      {value}
    </Typography>
  </Box>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState(null);
  const [certsSummary, setCertsSummary] = useState(null);
  const [certsLoading, setCertsLoading] = useState(true);
  const [attestationSummary, setAttestationSummary] = useState(null);
  const [attestationLoading, setAttestationLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      const dashboardResponse = await api.get("/api/reports/dashboard-summary");
      setSummary(dashboardResponse.data);

      setCertsLoading(true);
      Promise.race([
        api.get("/api/certs-secrets/summary"),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error("Timeout")), 10000),
        ),
      ])
        .then((response) => {
          setCertsSummary(response.data);
          setCertsLoading(false);
        })
        .catch(() => {
          setCertsSummary(null);
          setCertsLoading(false);
        });

      setAttestationLoading(true);
      api
        .get("/api/attestation-scheduling/schedule-summary")
        .then((response) => {
          setAttestationSummary(response.data);
          setAttestationLoading(false);
        })
        .catch((attestationError) => {
          console.error(
            "Failed to fetch attestation summary:",
            attestationError,
          );
          setAttestationSummary(null);
          setAttestationLoading(false);
        });
    } catch (fetchError) {
      console.error("Failed to fetch dashboard data:", fetchError);
      setError("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!summary) {
    return null;
  }

  const COLORS = [
    "#2563EB",
    "#059669",
    "#DC2626",
    "#7C3AED",
    "#EA580C",
    "#0891B2",
  ];

  const categoryGroupData = (summary.categoryGroupDistribution || [])
    .filter((item) => item && item.name && Number(item.license_count || 0) > 0)
    .map((item, index) => ({
      name: item.name || "Uncategorized",
      value: Number(item.license_count || 0),
      color: COLORS[index % COLORS.length],
    }));

  const activeNotExpiring =
    (summary.overall.active_licenses || 0) -
    (summary.overall.expiring_in_30_days || 0);

  const statusData = [
    {
      name: "Active",
      value: Math.max(0, activeNotExpiring),
      color: "#059669",
    },
    {
      name: "Expiring in 30 Days",
      value: Number(summary.overall.expiring_in_30_days || 0),
      color: "#EA580C",
    },
    {
      name: "Expired",
      value: Number(summary.overall.expired_licenses || 0),
      color: "#DC2626",
    },
    {
      name: "Cancelled",
      value: Number(summary.overall.cancelled_licenses || 0),
      color: "#6B7280",
    },
  ];

  const licensingStats = [
    { label: "Total Services", value: summary.overall.total_licenses },
    { label: "Active Services", value: summary.overall.active_licenses },
    {
      label: "Total Active Cost",
      value: formatDashboardCurrency(
        summary.overall.total_active_cost_gbp ||
          summary.overall.total_active_cost,
      ),
    },
    { label: "Expired", value: summary.overall.expired_licenses || 0 },
    {
      label: "Expiring Soon",
      value:
        summary.overall.expiring_in_30_days ||
        summary.overall.expiring_soon ||
        0,
    },
    {
      label: "Monthly Recurring",
      value: formatDashboardCurrency(
        summary.monthlyRecurringCostGbp || summary.monthlyRecurringCost,
      ),
    },
    {
      label: "Yearly Recurring",
      value: formatDashboardCurrency(
        summary.yearlyRecurringCostGbp || summary.yearlyRecurringCost,
      ),
    },
  ];

  const certsStats = certsSummary
    ? [
        { label: "Total", value: certsSummary.total || 0 },
        { label: "Certificates", value: certsSummary.certificates || 0 },
        { label: "Secrets", value: certsSummary.secrets || 0 },
        { label: "Expired", value: certsSummary.expired || 0 },
        { label: "Expiring Soon", value: certsSummary.expiringSoon || 0 },
      ]
    : null;

  const assetStats = summary.assetRegister
    ? [
        { label: "Total Assets", value: summary.assetRegister.total || 0 },
        {
          label: "Asset Owners",
          value: summary.assetRegister.uniqueOwners || 0,
        },
        {
          label: "Hardware",
          value: summary.assetRegister.byType?.hardware_assets || 0,
        },
        {
          label: "Azure Resources",
          value: summary.assetRegister.byType?.azure_resources || 0,
        },
        {
          label: "Managed Devices",
          value: summary.assetRegister.byType?.managed_devices || 0,
        },
        {
          label: "Unmanaged (BYOD)",
          value: summary.assetRegister.byType?.unmanaged_devices || 0,
        },
      ]
    : null;

  const softwareStats = summary.softwarePermissions
    ? [
        {
          label: "Total Software",
          value: summary.softwarePermissions.total || 0,
        },
        {
          label: "Active",
          value: summary.softwarePermissions.byStatus?.active || 0,
        },
        {
          label: "Permissions",
          value: summary.softwarePermissions.totalPermissions || 0,
        },
        {
          label: "Users",
          value: summary.softwarePermissions.uniqueUsers || 0,
        },
        { label: "Roles", value: summary.softwarePermissions.totalRoles || 0 },
        {
          label: "Internal Apps",
          value: summary.softwarePermissions.byType?.internal || 0,
        },
      ]
    : null;

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={1.5}
      height={{ xs: "auto", md: "calc(100dvh - 112px)" }}
      maxHeight={{ xs: "none", md: "calc(100dvh - 112px)" }}
      minHeight={0}
      minWidth={0}
      width="100%"
      overflow={{ xs: "visible", md: "auto" }}
      style={{ WebkitOverflowScrolling: "touch" }}
    >
      <Box
        display="grid"
        gap={1.5}
        flex="0 0 auto"
        minWidth={0}
        width="100%"
        gridTemplateColumns={{
          xs: "1fr",
          sm: "repeat(2, minmax(0, 1fr))",
          lg: "repeat(3, minmax(0, 1fr))",
        }}
        gridAutoRows="minmax(200px, auto)"
      >
        <DashboardInfoTile
          title="Business Services"
          to="/licenses"
          headerColor={DASHBOARD_TILE_STYLES.businessServices.headerColor}
          icon={<LicenseIcon {...TILE_ICON_PROPS} />}
          stats={licensingStats}
        />
        <DashboardInfoTile
          title="Certs & Secrets"
          to="/certs-secrets"
          headerColor={DASHBOARD_TILE_STYLES.certsSecrets.headerColor}
          icon={<SecurityIcon {...TILE_ICON_PROPS} />}
          loading={certsLoading}
          stats={certsStats}
          footer={<RefreshCaption value={certsSummary?.lastRefresh} />}
        />
        <DashboardInfoTile
          title="Asset Register"
          to="/asset-register"
          headerColor={DASHBOARD_TILE_STYLES.assetRegister.headerColor}
          icon={<ComputerIcon {...TILE_ICON_PROPS} />}
          stats={assetStats}
          footer={<RefreshCaption value={summary.assetRegister?.lastRefresh} />}
        />
        <DashboardInfoTile
          title="Software & Permissions"
          to="/software-permissions"
          headerColor={DASHBOARD_TILE_STYLES.softwarePermissions.headerColor}
          icon={<AppsIcon {...TILE_ICON_PROPS} />}
          stats={softwareStats}
          footer={
            <RefreshCaption value={summary.softwarePermissions?.lastRefresh} />
          }
        />
        <DashboardInfoTile
          title="Clients"
          to="/clients"
          headerColor={DASHBOARD_TILE_STYLES.clients.headerColor}
          icon={<ClientsIcon {...TILE_ICON_PROPS} />}
          stats={clientTileStats(summary.clients)}
        />
        <DashboardInfoTile
          title="AI Systems Register"
          to="/ai-systems"
          headerColor={DASHBOARD_TILE_STYLES.aiSystems.headerColor}
          icon={<AiSystemsIcon {...TILE_ICON_PROPS} />}
          stats={aiSystemsTileStats(summary.aiSystems)}
        />
      </Box>

      <Card
        elevation={2}
        role="link"
        tabIndex={0}
        aria-label="Open Attestation"
        onClick={() => navigate("/attestation")}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            navigate("/attestation");
          }
        }}
        sx={{
          flexShrink: 0,
          cursor: "pointer",
          transition: "transform 0.15s ease, box-shadow 0.15s ease",
          "&:hover": { transform: "translateY(-1px)", boxShadow: 4 },
          "&:focus-visible": {
            outline: "2px solid",
            outlineColor: "primary.main",
            outlineOffset: 2,
          },
        }}
      >
        <CardContent sx={{ py: 1.25, px: 2, "&:last-child": { pb: 1.25 } }}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <AssignmentIcon color="primary" sx={{ fontSize: 22 }} />
            <Typography variant="subtitle1" fontWeight={600}>
              Attestation
            </Typography>
          </Box>
          {attestationLoading ? (
            <Box display="flex" justifyContent="center" py={1}>
              <CircularProgress size={22} />
            </Box>
          ) : attestationSummary ? (
            <Box display="flex" flexWrap="wrap" gap={2}>
              <AttestationStat
                label="Schedule"
                value={attestationSummary.enabled ? "Enabled" : "Disabled"}
                valueColor={
                  attestationSummary.enabled ? "success.main" : "text.secondary"
                }
              />
              <AttestationStat
                label="Frequency"
                value={`${attestationSummary.frequencyMonths} mo (${
                  attestationSummary.scheduleType?.replace("_", " ") ||
                  "quarterly"
                })`}
              />
              <AttestationStat
                label="Next scheduled"
                value={
                  attestationSummary.nextScheduledDate
                    ? formatUkDate(attestationSummary.nextScheduledDate)
                    : "Not scheduled"
                }
              />
              <AttestationStat
                label="Pending"
                value={attestationSummary.pendingCount || 0}
                valueColor="warning.main"
              />
              <AttestationStat
                label="Yours"
                value={attestationSummary.pendingCountForUser ?? 0}
              />
              <AttestationStat
                label="Confirmed"
                value={attestationSummary.confirmedCount || 0}
              />
            </Box>
          ) : (
            <Alert severity="info">
              Attestation scheduling is not configured. Configure it in
              Notification Preferences.
            </Alert>
          )}
        </CardContent>
      </Card>

      <Box
        display="grid"
        gap={1.5}
        flex="1 1 auto"
        minHeight={{ xs: 260, md: 280 }}
        minWidth={0}
        width="100%"
        gridTemplateColumns={{ xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }}
      >
        <Card
          elevation={2}
          sx={{
            height: "100%",
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <CardContent
            sx={{
              py: 1.25,
              px: 2,
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              "&:last-child": { pb: 1.25 },
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} mb={0.5}>
              Licenses by Category Group
            </Typography>
            <Box flex={1} minHeight={0}>
              <LicenseDonutChart
                data={categoryGroupData}
                emptyMessage="No category group data available"
              />
            </Box>
          </CardContent>
        </Card>

        <Card
          elevation={2}
          sx={{
            height: "100%",
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <CardContent
            sx={{
              py: 1.25,
              px: 2,
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              "&:last-child": { pb: 1.25 },
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} mb={0.5}>
              License Status Distribution
            </Typography>
            <Box flex={1} minHeight={0}>
              <LicenseDonutChart
                data={statusData}
                legendItems={statusData}
                emptyMessage="No license status data available"
              />
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default Dashboard;
